import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Escenario, EscenarioResumen } from '../../types/Calculadora';
import { Card, Button, AnimatedBadge, Modal } from '../ui';

interface Props {
  escenarios: EscenarioResumen[];
  escenarioActivo: Escenario | null;
  /** Hay cambios sin guardar respecto del escenario cargado. */
  dirty: boolean;
  guardando: boolean;
  cargandoDatosReales: boolean;
  onSeleccionar: (id: number) => void;
  onNuevo: () => void;
  onGuardar: () => void;
  onGuardarComo: (nombre: string, descripcion: string) => void;
  onBorrar: () => void;
  onTraerDatosReales: () => void;
}

function formatFecha(iso: string): string {
  try {
    // SQLite devuelve 'YYYY-MM-DD HH:MM:SS' en UTC, sin zona explícita.
    const normalizado = iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`;
    return new Date(normalizado).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const EscenarioBar: React.FC<Props> = ({
  escenarios,
  escenarioActivo,
  dirty,
  guardando,
  cargandoDatosReales,
  onSeleccionar,
  onNuevo,
  onGuardar,
  onGuardarComo,
  onBorrar,
  onTraerDatosReales,
}) => {
  const [modal, setModal] = useState<'guardar-como' | 'borrar' | null>(null);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');

  const puedeEditar = escenarioActivo?.canEdit ?? false;

  const abrirGuardarComo = () => {
    setNombre(escenarioActivo ? `${escenarioActivo.name} (copia)` : 'Escenario nuevo');
    setDescripcion(escenarioActivo?.description ?? '');
    setModal('guardar-como');
  };

  const confirmarGuardarComo = () => {
    if (nombre.trim() === '') return;
    onGuardarComo(nombre.trim(), descripcion.trim());
    setModal(null);
  };

  return (
    <Card className="p-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <label htmlFor="escenario-select" className="text-xs font-semibold text-gray-500 shrink-0">
            Escenario
          </label>
          <select
            id="escenario-select"
            value={escenarioActivo?.id ?? ''}
            onChange={(e) => {
              if (e.target.value === '') onNuevo();
              else onSeleccionar(Number(e.target.value));
            }}
            className="flex-1 min-w-0 text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white
              focus:outline-none focus:ring-2 focus:ring-gappex-primary/30 focus:border-gappex-primary"
          >
            <option value="">— Borrador sin guardar —</option>
            {escenarios.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} · {e.createdBy}
              </option>
            ))}
          </select>

          <AnimatedBadge show={dirty} className="shrink-0 bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
            Sin guardar
          </AnimatedBadge>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onTraerDatosReales}
            disabled={cargandoDatosReales}
            title="Pisa suscriptores y cuotas con lo que hay hoy en Salesforce. No toca los precios de las prestaciones."
          >
            {cargandoDatosReales ? 'Trayendo…' : 'Traer datos reales'}
          </Button>

          <Button type="button" variant="secondary" onClick={abrirGuardarComo}>
            Guardar como…
          </Button>

          {escenarioActivo && puedeEditar && (
            <Button type="button" variant="primary" onClick={onGuardar} disabled={guardando || !dirty}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </Button>
          )}

          {escenarioActivo && puedeEditar && (
            <Button type="button" variant="danger" onClick={() => setModal('borrar')}>
              Borrar
            </Button>
          )}
        </div>
      </div>

      {escenarioActivo && (
        <p className="mt-2 text-[11px] text-gray-400">
          Creado por <strong className="text-gray-500">{escenarioActivo.createdBy}</strong> · última
          edición de <strong className="text-gray-500">{escenarioActivo.updatedBy}</strong> el{' '}
          {formatFecha(escenarioActivo.updatedAt)}
          {!puedeEditar && ' · solo lectura: guardalo como copia para modificarlo'}
        </p>
      )}

      <AnimatePresence>
        {modal === 'guardar-como' && (
          <Modal key="guardar-como" titulo="Guardar escenario" onClose={() => setModal(null)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="esc-nombre" className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Nombre
                </label>
                <input
                  id="esc-nombre"
                  type="text"
                  value={nombre}
                  autoFocus
                  maxLength={120}
                  onChange={(e) => setNombre(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && confirmarGuardarComo()}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2
                    focus:outline-none focus:ring-2 focus:ring-gappex-primary/30 focus:border-gappex-primary"
                />
              </div>
              <div>
                <label htmlFor="esc-desc" className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Descripción <span className="font-normal text-gray-400">(opcional)</span>
                </label>
                <textarea
                  id="esc-desc"
                  value={descripcion}
                  rows={3}
                  maxLength={500}
                  onChange={(e) => setDescripcion(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-none
                    focus:outline-none focus:ring-2 focus:ring-gappex-primary/30 focus:border-gappex-primary"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setModal(null)}>
                  Cancelar
                </Button>
                <Button type="button" variant="primary" onClick={confirmarGuardarComo} disabled={nombre.trim() === ''}>
                  Guardar
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {modal === 'borrar' && escenarioActivo && (
          <Modal key="borrar" titulo="Borrar escenario" onClose={() => setModal(null)}>
            <p className="text-sm text-gray-600">
              Se va a borrar <strong className="text-gappex-secondary">{escenarioActivo.name}</strong> para
              todos. No se puede deshacer.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <Button type="button" variant="secondary" onClick={() => setModal(null)}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => {
                  onBorrar();
                  setModal(null);
                }}
              >
                Borrar
              </Button>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </Card>
  );
};

export default EscenarioBar;
